import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Calendar,
  Clock,
  Settings,
  Bell,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Building2,
  CreditCard,
  BarChart3,
  Sparkles,
  FileText,
  DollarSign,
  MessageSquare,
  Trophy,
  FileSignature,
  Umbrella,
  MapPin,
  ArrowLeftRight,
  Brain,
  Search,
  Command,
  Home,
  Navigation
} from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import api from '../lib/api';

import LanguageSelector from './LanguageSelector';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const { user, logout, token, isSuperAdmin, isOrgAdmin, isWorker } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    if (!token) return;

    try {
      const response = await api.get('/notifications?unread_only=true');
      setNotifications(response.data.notifications || []);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [token, fetchNotifications]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const markAllRead = async () => {
    try {
      await api.put('/notifications/read-all', {});
      setNotifications([]);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const mainNav = [
    { icon: LayoutDashboard, label: t('dashboard'), path: '/dashboard' },
    { icon: Calendar, label: t('shifts'), path: '/shifts' },
    { icon: Clock, label: t('timeTracking'), path: '/time-tracking' },
    { icon: Users, label: t('employees'), path: '/employees', hide: isWorker },
    { icon: MapPin, label: t('locations'), path: '/locations' },
    { icon: Navigation, label: t('tracking') || 'Tracking', path: '/employee-tracking', hide: isWorker },
    { icon: ArrowLeftRight, label: t('swaps'), path: '/shift-swap', hide: !isWorker },
  ];

  const toolsNav = [
    { icon: MessageSquare, label: t('chat'), path: '/chat' },
    { icon: Umbrella, label: t('leave'), path: '/leave' },
    { icon: Trophy, label: t('gamification'), path: '/gamification' },
    { icon: FileText, label: t('documents'), path: '/hours' },
    { icon: FileSignature, label: t('signatures'), path: '/signatures' },
  ];

  const adminNav = [
    { icon: BarChart3, label: t('reports'), path: '/reports' },
    { icon: DollarSign, label: t('payroll'), path: '/payroll' },
    { icon: Brain, label: t('aiScheduling'), path: '/ai-auto-assign' },
    { icon: Sparkles, label: t('predictions'), path: '/ai-predictions' },
    { icon: Building2, label: t('organization'), path: '/organization' },
    { icon: CreditCard, label: t('subscription'), path: '/subscription' },
    { icon: Settings, label: t('settings'), path: '/settings' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-neutral-50/50">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-4 focus:left-4 focus:bg-brand-500 focus:text-white focus:px-4 focus:py-2 focus:rounded-lg">
        Skip to main content
      </a>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden"
          role="button"
          tabIndex={0}
          aria-label="Close sidebar"
          onClick={() => setSidebarOpen(false)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSidebarOpen(false); }}
        />
      )}

      {/* ===== SIDEBAR ===== */}
      <aside aria-label="Sidebar" className={`fixed top-0 left-0 h-full w-[260px] bg-white border-r border-neutral-100 z-50 transform transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {/* Logo */}
        <div className="h-[60px] flex items-center justify-between px-5 border-b border-neutral-100/80">
          <Link to="/dashboard" className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-md bg-neutral-950 flex items-center justify-center flex-shrink-0">
              <svg width="14" height="14" viewBox="0 0 512 512" fill="none">
                <defs>
                  <linearGradient id="sideGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{stopColor:'#635BFF'}}/>
                    <stop offset="100%" style={{stopColor:'#A78BFA'}}/>
                  </linearGradient>
                  <linearGradient id="sideAccent" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{stopColor:'#80E9FF'}}/>
                    <stop offset="100%" style={{stopColor:'#635BFF'}}/>
                  </linearGradient>
                </defs>
                <rect x="128" y="112" width="48" height="288" rx="8" fill="url(#sideGrad)"/>
                <path d="M 176 112 L 280 112 Q 340 112 340 172 Q 340 232 280 232 L 176 232"
                      fill="none" stroke="url(#sideGrad)" strokeWidth="48" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="240" y1="232" x2="370" y2="400"
                      stroke="url(#sideAccent)" strokeWidth="48" strokeLinecap="round"/>
                <circle cx="384" cy="136" r="16" fill="#80E9FF" opacity="0.8"/>
              </svg>
            </div>
            <span className="text-[14px] font-semibold text-neutral-950 tracking-tight truncate">
              {user?.organization?.name || 'Rostafy'}
            </span>
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1.5 text-neutral-400 hover:text-neutral-600 rounded-lg hover:bg-neutral-100"
            aria-label="Close sidebar"
            aria-expanded={sidebarOpen}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Search */}
        <div className="p-3">
          <button aria-label="Search" className="w-full flex items-center gap-2.5 px-3 py-2 bg-neutral-50 border border-neutral-200/60 rounded-lg text-neutral-400 text-[13px] hover:border-neutral-300 transition-colors">
            <Search className="w-3.5 h-3.5" />
            <span>{t('searchPlaceholder')}</span>
            <div className="ml-auto flex items-center gap-0.5 text-[11px] text-neutral-300 bg-white px-1.5 py-0.5 rounded border border-neutral-200/60">
              <Command className="w-2.5 h-2.5" />K
            </div>
          </button>
        </div>

        {/* Navigation */}
        <nav aria-label="Main navigation" className="px-3 py-1 space-y-5 overflow-y-auto scrollbar-hide" style={{ maxHeight: 'calc(100vh - 180px)' }}>
          <div>
            <p className="px-3 mb-1.5 text-[11px] font-medium text-neutral-400 uppercase tracking-[0.08em]">
              {t('mainSection')}
            </p>
            <div className="space-y-0.5">
              {mainNav.filter(item => !item.hide).map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-150 ${
                    isActive(item.path)
                      ? 'bg-brand-500/8 text-brand-500'
                      : 'text-neutral-500 hover:text-neutral-950 hover:bg-neutral-50'
                  }`}
                >
                  <item.icon className={`w-4 h-4 ${isActive(item.path) ? 'text-brand-500' : ''}`} strokeWidth={1.75} />
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          <div>
            <p className="px-3 mb-1.5 text-[11px] font-medium text-neutral-400 uppercase tracking-[0.08em]">
              {t('toolsSection')}
            </p>
            <div className="space-y-0.5">
              {toolsNav.map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-150 ${
                    isActive(item.path)
                      ? 'bg-brand-500/8 text-brand-500'
                      : 'text-neutral-500 hover:text-neutral-950 hover:bg-neutral-50'
                  }`}
                >
                  <item.icon className={`w-4 h-4 ${isActive(item.path) ? 'text-brand-500' : ''}`} strokeWidth={1.75} />
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          {(isOrgAdmin || isSuperAdmin) && (
            <div>
              <p className="px-3 mb-1.5 text-[11px] font-medium text-neutral-400 uppercase tracking-[0.08em]">
                {t('adminSection')}
              </p>
              <div className="space-y-0.5">
                {adminNav.map(item => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-150 ${
                      isActive(item.path)
                        ? 'bg-brand-500/8 text-brand-500'
                        : 'text-neutral-500 hover:text-neutral-950 hover:bg-neutral-50'
                    }`}
                  >
                    <item.icon className={`w-4 h-4 ${isActive(item.path) ? 'text-brand-500' : ''}`} strokeWidth={1.75} />
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </nav>

        {/* User card */}
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-neutral-100/80 bg-white">
          <div className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-neutral-50 cursor-pointer transition-colors">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-500 to-brand-400 flex items-center justify-center text-white text-[12px] font-semibold flex-shrink-0">
              {user?.first_name?.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-medium text-neutral-950 truncate">{user?.first_name} {user?.last_name}</p>
              <p className="text-[11px] text-neutral-400 capitalize truncate">{user?.role?.replace('_', ' ')}</p>
            </div>
            <ChevronDown className="w-3.5 h-3.5 text-neutral-300" />
          </div>
        </div>
      </aside>

      {/* ===== MAIN CONTENT ===== */}
      <div className="lg:ml-[260px]">
        {/* Header */}
        <header role="banner" className="h-[60px] bg-white/80 backdrop-blur-xl border-b border-neutral-100/80 flex items-center justify-between px-6 sticky top-0 z-30">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
            aria-label="Open sidebar"
            aria-expanded={sidebarOpen}
          >
            <Menu className="w-5 h-5" />
          </button>

          <div className="flex-1" />

          <div className="flex items-center gap-2">
            <LanguageSelector compact={true} />

            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 text-neutral-400 hover:text-neutral-600 rounded-lg hover:bg-neutral-100 transition-colors"
                aria-label="Notifications"
                aria-expanded={showNotifications}
              >
                <Bell className="w-[18px] h-[18px]" strokeWidth={1.75} />
                {notifications.length > 0 && (
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white" aria-live="polite" role="status">
                    <span className="sr-only">{notifications.length} unread notifications</span>
                  </span>
                )}
              </button>

              {showNotifications && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-neutral-200/80 rounded-xl shadow-xl z-50 overflow-hidden">
                  <div className="p-4 border-b border-neutral-100 flex items-center justify-between">
                    <span className="text-[14px] font-semibold text-neutral-950">{t('notifications')}</span>
                    {notifications.length > 0 && (
                      <button onClick={markAllRead} className="text-[12px] text-brand-500 hover:text-brand-500/80 font-medium transition-colors">
                        {t('markAllRead')}
                      </button>
                    )}
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-8 text-center">
                        <Bell className="w-7 h-7 text-neutral-200 mx-auto mb-2" />
                        <p className="text-neutral-400 text-[13px]">{t('noNotifications')}</p>
                      </div>
                    ) : (
                      notifications.map(n => (
                        <div key={n.id} className="p-4 border-b border-neutral-50 last:border-0 hover:bg-neutral-50/50 transition-colors">
                          <p className="text-[13px] font-medium text-neutral-950">{n.title}</p>
                          <p className="text-[12px] text-neutral-400 mt-1">{n.message}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* User */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-neutral-100 transition-colors"
                aria-label="User menu"
                aria-expanded={showUserMenu}
              >
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-brand-500 to-brand-400 flex items-center justify-center text-white text-[11px] font-semibold">
                  {user?.first_name?.charAt(0)}
                </div>
              </button>

              {showUserMenu && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-neutral-200/80 rounded-xl shadow-xl z-50 overflow-hidden">
                  <div className="p-4 border-b border-neutral-100">
                    <p className="text-[14px] font-medium text-neutral-950">{user?.first_name} {user?.last_name}</p>
                    <p className="text-[12px] text-neutral-400">{user?.email}</p>
                  </div>
                  <div className="p-1.5">
                    <Link
                      to="/settings"
                      onClick={() => setShowUserMenu(false)}
                      className="flex items-center gap-2.5 px-3 py-2 text-[13px] text-neutral-600 hover:bg-neutral-50 rounded-lg transition-colors"
                    >
                      <Settings className="w-4 h-4" strokeWidth={1.75} />
                      {t('settings')}
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2.5 px-3 py-2 text-[13px] text-red-600 hover:bg-red-50 rounded-lg w-full transition-colors"
                    >
                      <LogOut className="w-4 h-4" strokeWidth={1.75} />
                      {t('logout')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <main id="main-content" role="main" className="p-6">
          {children}
        </main>
      </div>

      {/* ===== MOBILE NAV ===== */}
      <nav aria-label="Mobile navigation" className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-xl border-t border-neutral-100 lg:hidden z-40 safe-area-inset-bottom">
        <div className="flex items-center justify-around py-1.5">
          {[
            { icon: Home, label: t('mobileHome'), path: '/dashboard' },
            { icon: Calendar, label: t('mobileShifts'), path: '/shifts' },
            { icon: Clock, label: t('mobileClock'), path: '/time-tracking' },
            ...(isWorker ? [] : [{ icon: Users, label: t('mobileTeam'), path: '/employees' }]),
            { icon: Menu, label: t('mobileMore'), action: () => setSidebarOpen(true) }
          ].map((item, i) => (
            item.action ? (
              <button
                key={i}
                onClick={item.action}
                className="flex flex-col items-center gap-0.5 p-2 text-neutral-400"
              >
                <item.icon className="w-5 h-5" strokeWidth={1.75} />
                <span className="text-[10px] font-medium">{item.label}</span>
              </button>
            ) : (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center gap-0.5 p-2 transition-colors ${
                  isActive(item.path) ? 'text-brand-500' : 'text-neutral-400'
                }`}
              >
                <item.icon className="w-5 h-5" strokeWidth={1.75} />
                <span className="text-[10px] font-medium">{item.label}</span>
              </Link>
            )
          ))}
        </div>
      </nav>
    </div>
  );
};

export default Layout;
